<template>
  <!-- Live "can this bus help?" context for one of the SOS's own nearby vehicles, fetched on demand
       when the dispatcher expands it. Shared by the sender and receiver detail pages. Emits the
       last-hour breadcrumb up so the parent map can draw the trail. -->
  <div class="svc">
    <div v-if="loading" class="svc-state"><b-spinner small></b-spinner>&nbsp;Reading live status&hellip;</div>
    <div v-else-if="error" class="svc-state svc-state--error"><i class="fa fa-exclamation-triangle"></i>&nbsp;Couldn't read this bus's live status.</div>

    <template v-else-if="ctx">
      <!-- candidacy call-out: the passenger estimate drives whether this bus is a good candidate -->
      <div class="svc-verdict" :class="'svc-verdict--' + passengerVariant">
        <i class="fa" :class="passengerIcon"></i>
        <div class="svc-verdict-body">
          <div class="svc-verdict-label">{{ passengers.label }}</div>
          <div v-if="passengers.reason" class="svc-verdict-reason">{{ passengers.reason }}</div>
        </div>
      </div>

      <!-- live position: speed + heading + freshness -->
      <div class="svc-row">
        <span class="svc-k">Now</span>
        <span class="svc-v">
          <span class="svc-speed">{{ speedText }}</span>
          <span v-if="position.heading_compass" class="svc-heading">
            <i class="fa fa-location-arrow svc-arrow" :style="arrowStyle"></i>{{ position.heading_compass }}
          </span>
          <span class="svc-fix" :class="{ 'svc-fix--stale': fixStale }">{{ fixText }}</span>
        </span>
      </div>
      <div v-if="position.address" class="svc-row">
        <span class="svc-k">At</span>
        <span class="svc-v svc-addr" :title="position.address">{{ position.address }}</span>
      </div>
      <div class="svc-row">
        <span class="svc-k">From SOS</span>
        <span class="svc-v">
          <template v-if="position.distance_from_sos_mi != null">{{ position.distance_from_sos_mi }} mi away</template>
          <template v-else>—</template>
          <span v-if="drive && drive.time" class="svc-drive" title="Live driving time from Google (with current traffic)">· {{ drive.time }} drive</span>
          <span v-else-if="position.drive_time" class="svc-drive svc-drive--est" title="Estimated from straight-line distance">· ~{{ position.drive_time }} drive (est.)</span>
        </span>
      </div>

      <!-- active booking / itinerary the bus is on right now -->
      <div v-if="booking && booking.active" class="svc-book">
        <div class="svc-book-head">
          <i class="fa fa-route"></i>&nbsp;<span>On a booking</span>
          <span v-if="bookingMeta" class="svc-book-meta">{{ bookingMeta }}</span>
        </div>
        <div v-for="(leg, i) in (booking.legs || [])" :key="i" class="svc-leg" :class="{ 'svc-leg--active': isActiveLeg(leg) }">
          <div v-if="leg.description" class="svc-leg-desc">{{ leg.description }}</div>
          <div v-if="leg.begin_time || leg.begin_address" class="svc-stop">
            <span class="svc-stop-kind">Pickup</span><span class="svc-stop-time">{{ fmtTime(leg.begin_time) }}</span>
            <span class="svc-stop-addr" :title="leg.begin_address">{{ leg.begin_address || '—' }}</span>
          </div>
          <div v-if="leg.arrival_time || leg.arrival_address" class="svc-stop">
            <span class="svc-stop-kind">Drop-off</span><span class="svc-stop-time">{{ fmtTime(leg.arrival_time) }}</span>
            <span class="svc-stop-addr" :title="leg.arrival_address">{{ leg.arrival_address || '—' }}</span>
          </div>
          <div v-if="leg.end_time || leg.end_address" class="svc-stop">
            <span class="svc-stop-kind">Return</span><span class="svc-stop-time">{{ fmtTime(leg.end_time) }}</span>
            <span class="svc-stop-addr" :title="leg.end_address">{{ leg.end_address || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- driver + HOS (signed-in only; withheld on the public share-token view) -->
      <div v-if="ctx.driver_hidden" class="svc-hos svc-hos--muted">
        <i class="fa fa-user-clock"></i>&nbsp;Sign in to see the driver and their hours of service.
      </div>
      <div v-else class="svc-hos">
        <div class="svc-hos-head">
          <span class="svc-driver"><i class="fa fa-user"></i>&nbsp;{{ driverName }}<i
            v-if="driverVerified === true" class="fa fa-check-circle svc-driver-ok"
            title="Confirmed on this bus by telematics"></i><i
            v-else-if="driverVerified === false" class="fa fa-exclamation-triangle svc-driver-warn"
            title="Assigned to this bus, but telematics shows this driver on a different vehicle"></i></span>
          <a v-if="driverPhone" :href="'tel:' + driverPhone" class="svc-driver-phone"><i class="fa fa-phone"></i>&nbsp;{{ driverPhone }}</a>
        </div>
        <template v-if="hos && hos.available">
          <div class="svc-duty">
            <span class="svc-duty-pill" :class="'svc-duty-pill--' + (hos.duty_status || 'unknown')">{{ hos.duty_status_label }}</span>
            <span v-if="hos.status_elapsed_minutes != null" class="svc-duty-since">for {{ fmtDur(hos.status_elapsed_minutes) }}</span>
          </div>
          <div class="svc-hos-times">
            <span v-if="hos.on_duty_since">On duty since {{ fmtTime(hos.on_duty_since) }}</span>
            <span v-if="hos.last_off_duty">· last off duty {{ fmtTime(hos.last_off_duty) }}</span>
          </div>
          <div v-if="hos.on_duty_minutes != null" class="svc-hos-tally">
            <div class="svc-hos-metric">
              <span class="svc-hos-metric-k">On duty</span>
              <div class="svc-hos-bar"><span class="svc-hos-bar-fill" :class="{ 'svc-hos-bar-fill--warn': onDutyHigh }" :style="{ width: onDutyPct + '%' }"></span></div>
              <span class="svc-hos-metric-v" :class="{ 'svc-hos-tally-label--warn': onDutyHigh }">{{ fmtDur(hos.on_duty_minutes) }} / 14h</span>
            </div>
            <div class="svc-hos-metric">
              <span class="svc-hos-metric-k">Driving</span>
              <div class="svc-hos-bar"><span class="svc-hos-bar-fill" :class="{ 'svc-hos-bar-fill--warn': drivingHigh }" :style="{ width: drivingPct + '%' }"></span></div>
              <span class="svc-hos-metric-v" :class="{ 'svc-hos-tally-label--warn': drivingHigh }">{{ fmtDur(hos.drive_minutes) }} / 11h</span>
            </div>
          </div>
          <div class="svc-hos-note">{{ hos.note }}</div>
        </template>
        <div v-else class="svc-hos-na">{{ (hos && hos.reason) || 'HOS not available.' }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { get_sos_vehicle_context, get_sos_vehicle_context_by_token } from '../networking/apis'

export default {
  name: 'sos_vehicle_context',
  props: {
    vehicle: { type: Object, required: true },   // the internal loc item ({ name, veh_stat, ... })
    standalone: { type: Boolean, default: false },
    token: { type: String, default: '' },        // per-event share token (standalone)
    requestId: { type: String, default: '' },    // authed addressing
    tenant: { type: String, default: '' },        // viewing tenant id (authed)
  },
  data() {
    return { loading: true, error: false, ctx: null }
  },
  computed: {
    vehicleId() {
      const vs = this.vehicle && this.vehicle.veh_stat
      return (vs && (vs.id != null ? String(vs.id) : null)) || (this.vehicle && this.vehicle.name) || ''
    },
    position() { return (this.ctx && this.ctx.position) || {} },
    drive() { return (this.ctx && this.ctx.drive) || null },
    booking() { return (this.ctx && this.ctx.booking) || null },
    passengers() { return (this.ctx && this.ctx.passengers) || {} },
    hos() { return (this.ctx && this.ctx.hos) || null },
    driverName() { return (this.ctx && this.ctx.driver && this.ctx.driver.name) || 'Driver not identified' },
    driverVerified() {
      // true = telematics confirms this driver is on this bus; false = telematics shows them on
      // another vehicle; null = couldn't check (no clocks/logs)
      const d = this.ctx && this.ctx.driver
      return d && typeof d.verified_current === 'boolean' ? d.verified_current : null
    },
    driverPhone() {
      const p = this.ctx && this.ctx.driver && this.ctx.driver.phone
      return p && /\d{3}/.test(p) ? p : ''
    },
    speedText() {
      const s = this.position.speed_mph
      if (s == null) return 'Speed unknown'
      return s >= 3 ? (s + ' mph') : 'Stopped'
    },
    fixText() {
      // the explicit fix date/time (in the bus's timezone), with the relative age for freshness
      const abs = this.position.time ? this.fmtTime(this.position.time) : null
      const a = this.position.age_minutes
      let rel = null
      if (a == null) rel = this.position.live ? 'live' : null
      else if (a <= 1) rel = 'just now'
      else if (a < 60) rel = a + ' min ago'
      else rel = Math.floor(a / 60) + 'h ' + (a % 60) + 'm ago'
      if (abs && rel) return abs + ' (' + rel + ')'
      return abs || rel || 'as of the SOS'
    },
    fixStale() { return this.position.age_minutes != null && this.position.age_minutes > 15 },
    arrowStyle() {
      // fa-location-arrow points NE (~45°); offset so heading 0 (North) points up
      const deg = Number(this.position.heading_deg || 0) - 45
      return { transform: 'rotate(' + deg + 'deg)' }
    },
    passengerVariant() {
      const map = { carrying: 'busy', dropped_off: 'free', trip_complete: 'free',
                    pre_trip: 'soft', no_active_trip: 'free', unknown: 'soft' }
      return map[this.passengers.status] || 'soft'
    },
    passengerIcon() {
      return this.passengerVariant === 'busy' ? 'fa-users'
        : this.passengerVariant === 'free' ? 'fa-check-circle' : 'fa-question-circle'
    },
    bookingMeta() {
      if (!this.booking) return ''
      const parts = []
      if (this.booking.label) parts.push(this.booking.label)
      if (this.booking.booking_id) parts.push('#' + this.booking.booking_id)
      return parts.join(' · ')
    },
    onDutyPct() {
      // on-duty this shift vs the 14h passenger-carrier on-duty window (840 min)
      const m = (this.hos && this.hos.on_duty_minutes) || 0
      return Math.min(100, Math.round((m / 840) * 100))
    },
    drivingPct() {
      // driving this shift vs the 11h driving limit (660 min)
      const m = (this.hos && this.hos.drive_minutes) || 0
      return Math.min(100, Math.round((m / 660) * 100))
    },
    onDutyHigh() {
      const m = this.hos && this.hos.on_duty_minutes
      return m != null && m >= 720   // 12h+ on duty — nearing the 14h limit
    },
    drivingHigh() {
      const m = this.hos && this.hos.drive_minutes
      return m != null && m >= 540   // 9h+ driving — nearing the 11h limit
    },
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      this.error = false
      try {
        const vid = this.vehicleId
        let data = {}
        if (this.standalone) {
          const { value: { data: d = {} } } = await get_sos_vehicle_context_by_token(this.token, vid)
          data = d
        } else {
          const { value: { data: d = {} } } = await get_sos_vehicle_context(this.tenant, this.requestId, vid)
          data = d
        }
        this.ctx = data
        this.$emit('breadcrumb', (data && data.breadcrumb) || [])
      } catch (e) {
        this.error = true
      }
      this.loading = false
    },
    isActiveLeg(leg) {
      const a = this.booking && this.booking.active_leg
      return !!(a && a.begin_time && leg.begin_time === a.begin_time)
    },
    fmtTime(iso) {
      if (!iso) return '—'
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) return iso
      // show every live-status time in the bus's local timezone (matching the map marker), not the
      // viewer's browser timezone, and always spell out the date so on-duty / booking times are
      // unambiguous across midnight
      const tz = (this.ctx && this.ctx.timezone) || undefined
      const date = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz })
      const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })
      return date + ', ' + time
    },
    fmtDur(minutes) {
      if (minutes == null) return '—'
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return h ? (h + 'h ' + (m ? m + 'm' : '').trim()).trim() : (m + 'm')
    },
  },
}
</script>

<style scoped>
.svc {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 8px 10px 10px;
  font-size: 13px;
  color: var(--fi-text);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.svc-state { color: var(--fi-text-faint); padding: 4px 0; }
.svc-state--error { color: var(--fi-danger); }

.svc-verdict {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  line-height: 1.35;
}
.svc-verdict .fa { margin-top: 2px; }
.svc-verdict--busy { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.svc-verdict--free { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.svc-verdict--soft { background: var(--fi-surface-sunken); color: var(--fi-text-muted); }
.svc-verdict-label { font-weight: 700; }
.svc-verdict-reason { font-weight: 400; font-size: 12.5px; opacity: 0.95; }

.svc-row { display: flex; gap: 8px; align-items: baseline; }
.svc-k {
  flex: 0 0 62px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.svc-v { min-width: 0; display: flex; flex-wrap: wrap; gap: 4px 8px; align-items: baseline; }
.svc-speed { font-weight: 700; color: var(--fi-text); font-variant-numeric: tabular-nums; }
.svc-heading { color: var(--fi-text-muted); }
.svc-arrow { color: var(--fi-rail-accent); margin-right: 2px; display: inline-block; }
.svc-fix { color: var(--fi-text-faint); font-size: 12px; }
.svc-fix--stale { color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.svc-drive { color: var(--fi-text-faint); }
.svc-drive--est { font-style: italic; opacity: .85; }
.svc-addr { color: var(--fi-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.svc-book { background: var(--fi-surface-sunken); border: 1px solid var(--fi-line); border-radius: 8px; padding: 7px 9px; }
.svc-book-head { display: flex; align-items: center; gap: 6px; font-weight: 700; color: color-mix(in srgb, #23608f 55%, var(--fi-text)); margin-bottom: 4px; }
.svc-book-meta { font-weight: 400; font-size: 12px; color: var(--fi-text-muted); }
.svc-leg { padding: 3px 0; }
.svc-leg--active { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); border-radius: 6px; padding: 4px 6px; }
.svc-leg-desc { font-weight: 600; font-size: 12.5px; color: var(--fi-text); }
.svc-stop { display: flex; gap: 6px; align-items: baseline; line-height: 1.4; }
.svc-stop-kind {
  flex: 0 0 54px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--fi-text-faint);
}
.svc-stop-time { flex: 0 0 auto; font-variant-numeric: tabular-nums; color: var(--fi-text); }
.svc-stop-addr { color: var(--fi-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }

.svc-hos { background: var(--fi-surface-sunken); border: 1px solid var(--fi-line); border-radius: 8px; padding: 7px 9px; }
.svc-hos--muted { color: var(--fi-text-faint); background: var(--fi-surface-sunken); }
.svc-hos-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.svc-driver { font-weight: 700; color: var(--fi-text); }
.svc-driver-ok { color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); margin-left: 5px; }
.svc-driver-warn { color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); margin-left: 5px; }
.svc-driver-phone { color: var(--fi-link); text-decoration: none; font-weight: 600; }
.svc-driver-phone:hover { text-decoration: underline; }
.svc-duty { display: flex; align-items: baseline; gap: 8px; margin-top: 4px; }
.svc-duty-pill {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--fi-surface-sunken);
  color: var(--fi-text-muted);
}
.svc-duty-pill--driving { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.svc-duty-pill--onDuty, .svc-duty-pill--on_duty { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.svc-duty-pill--offDuty, .svc-duty-pill--off_duty { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.svc-duty-pill--sleeper, .svc-duty-pill--sleeperBerth { background: color-mix(in srgb, #6a4fb3 12%, var(--fi-surface)); color: color-mix(in srgb, #5a44a0 55%, var(--fi-text)); }
.svc-duty-since { color: var(--fi-text-muted); }
.svc-hos-times { color: var(--fi-text-muted); font-size: 12px; margin-top: 3px; display: flex; flex-wrap: wrap; gap: 2px 6px; }
.svc-hos-tally { margin-top: 6px; display: flex; flex-direction: column; gap: 5px; }
.svc-hos-metric { display: flex; align-items: center; gap: 8px; }
.svc-hos-metric-k {
  flex: 0 0 52px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--fi-text-faint);
}
.svc-hos-bar { flex: 1 1 auto; height: 6px; border-radius: 3px; background: var(--fi-line-strong); overflow: hidden; }
.svc-hos-bar-fill { display: block; height: 100%; background: var(--fi-rail-accent); }
.svc-hos-bar-fill--warn { background: var(--fi-warning-accent); }
.svc-hos-metric-v {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--fi-text-muted);
  font-variant-numeric: tabular-nums;
  min-width: 70px;
  text-align: right;
}
.svc-hos-tally-label--warn { color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); font-weight: 600; }
.svc-hos-note { font-size: 11px; color: var(--fi-text-faint); margin-top: 3px; }
.svc-hos-na { color: var(--fi-text-faint); font-size: 12px; margin-top: 3px; }
</style>
